import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import {
  createPlan,
  getPlansList,
  getAllAdvisorSubscriptions,
} from "../../Services/phase2Service";

// Must match the Postgres subscription_duration_enum values.
const DURATIONS = ["Monthly", "Yearly"];

const emptyPlan = {
  title: "",
  description: "",
  plan_benefits: "",
  duration: "Yearly",
  price: "",
  tax_percentage: "18",
};

const SubscriptionsAdmin = () => {
  const [tab, setTab] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(emptyPlan);
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    try {
      const res = await getPlansList({ page: 1, perPage: 50 });
      setPlans(res?.data || res?.data?.data || []);
    } catch {
      // ignore
    }
  };
  const loadSubs = async () => {
    try {
      setSubs((await getAllAdvisorSubscriptions()) || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadPlans();
    loadSubs();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const benefits = form.plan_benefits
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await createPlan({
        title: form.title,
        description: form.description,
        plan_benefits: benefits,
        duration: form.duration,
        price: Number(form.price),
        tax_percentage: Number(form.tax_percentage),
      });
      toast.success("Plan created");
      setForm(emptyPlan);
      loadPlans();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Failed to create plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex gap-2 mb-6">
        {["plans", "subscribers"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
              tab === t ? "bg-[#425F80] text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "plans" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form
            onSubmit={submit}
            className="bg-white rounded-xl border border-gray-100 p-5 space-y-3"
          >
            <h3 className="font-semibold text-gray-800">Create Pro Plan</h3>
            <input
              required
              placeholder="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Benefits (one per line)"
              value={form.plan_benefits}
              onChange={(e) => setForm({ ...form, plan_benefits: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-24"
            />
            <select
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <div className="flex gap-3">
              <input
                type="number"
                required
                placeholder="Price ₹"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-1/2 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
              <input
                type="number"
                placeholder="Tax %"
                value={form.tax_percentage}
                onChange={(e) => setForm({ ...form, tax_percentage: e.target.value })}
                className="w-1/2 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-[#425F80] text-white rounded-lg py-2 text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create Plan"}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-3">
            {plans.length === 0 && <p className="text-gray-400">No plans yet.</p>}
            {plans.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex justify-between">
                  <h4 className="font-semibold text-gray-800">{p.title}</h4>
                  <span className="font-bold text-gray-900">
                    ₹{Number(p.total_price ?? p.price).toLocaleString("en-IN")}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{p.description}</p>
                <p className="text-xs text-gray-400 mt-1">Duration: {p.duration}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3">Advisor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Start</th>
                <th className="px-4 py-3">End</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                    No subscriptions yet.
                  </td>
                </tr>
              )}
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-gray-50">
                  <td className="px-4 py-3">{String(s.advisor_id).slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        s.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {s.start_date ? moment(s.start_date).format("DD MMM YYYY") : "-"}
                  </td>
                  <td className="px-4 py-3">
                    {s.end_date ? moment(s.end_date).format("DD MMM YYYY") : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsAdmin;
